<template>
  <li class="card integration-item" :class="{ 'item-disabled': !integration.enabled }">

    <!-- Режим редактирования -->
    <template v-if="isEditing">
      <h4 class="form-title">Редактирование интеграции Saby</h4>

      <div class="form-group">
        <base-label for="saby-edit-login" :required="true" :filled="!!form.login">
          Логин
        </base-label>
        <base-input
          id="saby-edit-login"
          v-model="form.login"
          type="text"
          :invalid="errors.login"
        />
        <span class="error-message" v-if="errors.login">
          Поле обязательно для заполнения
        </span>
      </div>

      <div class="form-group">
        <base-label for="saby-edit-org-name" :filled="!!form.org_name">
          Название нашей организации
        </base-label>
        <base-party-autocomplete
          v-model="form.org_name"
          placeholder="Введите название или ИНН организации"
          @select="handlePartySelect"
          @update:modelValue="form.org_name = $event || ''"
        />
      </div>

      <div class="form-group">
        <base-label for="saby-edit-password">Пароль</base-label>
        <base-input
          id="saby-edit-password"
          v-model="form.password"
          :is-password="true"
          placeholder="Оставьте пустым, чтобы не менять"
        />
      </div>

      <div class="form-grid">
        <div class="form-group">
          <base-label
            for="saby-edit-org-inn"
            :required="true"
            :filled="isInnFilled"
          >
            ИНН нашей организации
          </base-label>
          <base-input
            id="saby-edit-org-inn"
            v-model="form.org_inn"
            placeholder="10 цифр для ЮЛ или 12 для ИП"
            :invalid="errors.org_inn"
          />
          <span class="error-message" v-if="errors.org_inn">
            ИНН должен содержать 10 или 12 цифр
          </span>
        </div>

        <div class="form-group">
          <base-label
            for="saby-edit-org-kpp"
            :required="requiresKpp"
            :filled="isKppFilled"
          >
            КПП нашей организации
          </base-label>
          <base-input
            id="saby-edit-org-kpp"
            v-model="form.org_kpp"
            placeholder="Заполняется для юрлица"
            :invalid="errors.org_kpp"
          />
          <span class="field-hint">
            Для ИП с ИНН из 12 цифр КПП не нужен.
          </span>
          <span class="error-message" v-if="errors.org_kpp">
            КПП должен содержать 9 цифр
          </span>
        </div>
      </div>

      <div class="form-actions">
        <base-primary-button @click="save" :disabled="isLoading">Сохранить</base-primary-button>
        <base-button @click="cancel" :disabled="isLoading">Отмена</base-button>
      </div>
    </template>

    <!-- Режим просмотра -->
    <template v-else>
      <div class="tracker-main">
        <div class="tracker-info">
          <div class="tracker-top">
            <span class="tracker-name">Интеграция Saby</span>
          </div>
          <div class="tracker-field">
            <span class="tracker-field-label">Логин:</span>
            <span class="tracker-field-value">{{ integration.login }}</span>
          </div>
          <div class="tracker-field">
            <span class="tracker-field-label">Название нашей организации:</span>
            <span class="tracker-field-value">
              {{ integration.org_name || "Не заполнено" }}
            </span>
          </div>
          <div class="tracker-field">
            <span class="tracker-field-label">ИНН нашей организации:</span>
            <span class="tracker-field-value">
              {{ integration.org_inn || "Не заполнен" }}
            </span>
          </div>
          <div class="tracker-field">
            <span class="tracker-field-label">КПП нашей организации:</span>
            <span class="tracker-field-value">
              {{ integration.org_kpp || "Не требуется / не заполнен" }}
            </span>
          </div>
        </div>

        <div class="tracker-actions" @click.stop>
          <button
            class="action-btn action-btn--edit"
            title="Редактировать"
            :disabled="isLoading"
            @click="startEdit"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>

          <base-toggle-switch
            :model-value="integration.enabled"
            @update:model-value="$emit('toggle', integration)"
            :disabled="isLoading"
          />
        </div>
      </div>

      <div class="tracker-meta" v-if="integration.created_at">
        <MetaTime :created-at="integration.created_at" />
      </div>
    </template>

  </li>
</template>

<script>
import MetaTime from "@/components/Meta/MetaTime.vue";

export default {
  name: "SabyIntegrationCard",
  components: { MetaTime },
  props: {
    integration: { type: Object, required: true },
    isLoading: { type: Boolean, default: false },
  },
  emits: ["toggle", "save"],
  data() {
    return {
      isEditing: false,
      form: {
        login: "",
        password: "",
        org_name: "",
        org_inn: "",
        org_kpp: "",
      },
      errors: {
        login: false,
        org_inn: false,
        org_kpp: false,
      },
    };
  },
  computed: {
    normalizedInn() {
      return this.normalizeDigits(this.form.org_inn);
    },
    normalizedKpp() {
      return this.normalizeDigits(this.form.org_kpp);
    },
    requiresKpp() {
      return this.normalizedInn.length === 10;
    },
    isInnFilled() {
      return [10, 12].includes(this.normalizedInn.length);
    },
    isKppFilled() {
      return !this.requiresKpp || this.normalizedKpp.length === 9;
    },
  },
  methods: {
    normalizeDigits(value) {
      return String(value || "").replace(/\D/g, "");
    },

    handlePartySelect(suggestion) {
      if (!suggestion) {
        this.form.org_name = "";
        this.form.org_inn = "";
        this.form.org_kpp = "";
        return;
      }

      const data = suggestion.data || {};
      this.form.org_name =
        data.name?.short_with_opf || suggestion.value || "";
      this.form.org_inn = data.inn || "";
      this.form.org_kpp = data.kpp || "";
      this.errors.org_inn = false;
      this.errors.org_kpp = false;
    },

    startEdit() {
      this.form = {
        login: this.integration.login || "",
        password: "",
        org_name: this.integration.org_name || "",
        org_inn: this.integration.org_inn || "",
        org_kpp: this.integration.org_kpp || "",
      };
      this.errors = { login: false, org_inn: false, org_kpp: false };
      this.isEditing = true;
    },
    cancel() {
      this.isEditing = false;
    },
    validate() {
      this.errors = { login: false, org_inn: false, org_kpp: false };
      let isValid = true;

      if (!String(this.form.login || "").trim()) {
        this.errors.login = true;
        isValid = false;
      }

      if (![10, 12].includes(this.normalizedInn.length)) {
        this.errors.org_inn = true;
        isValid = false;
      }

      if (this.normalizedKpp && this.normalizedKpp.length !== 9) {
        this.errors.org_kpp = true;
        isValid = false;
      }

      if (this.requiresKpp && this.normalizedKpp.length !== 9) {
        this.errors.org_kpp = true;
        isValid = false;
      }

      return isValid;
    },
    save() {
      if (!this.validate()) return;

      const payload = {
        login: this.form.login,
        org_name: String(this.form.org_name || "").trim() || null,
        org_inn: this.normalizedInn,
        org_kpp: this.normalizedKpp || null,
      };
      if (this.form.password.trim()) payload.password = this.form.password;
      this.$emit("save", { id: this.integration.id, payload });
      this.isEditing = false;
    },
  },
};
</script>

<style scoped>
.item-disabled {
  background-color: rgba(186, 186, 186, 0.35);
}

.tracker-main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.tracker-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.tracker-top {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.tracker-name {
  font-weight: 600;
  font-size: 17px;
}

.tracker-field {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

.tracker-field-label {
  color: #666;
}

.tracker-field-value {
  color: var(--color-dark);
}

.tracker-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #6b7280;
  background: none;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.action-btn--edit:hover:not(:disabled) {
  background: #dbeafe;
  color: var(--color-blue);
}

.tracker-meta {
  margin-top: var(--base-in-indent);
}

.form-title {
  margin-bottom: var(--base-in-indent);
  color: #666;
}

.form-group {
  margin-bottom: var(--base-in-indent);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--base-in-indent);
}

.error-message {
  display: block;
  margin-top: 5px;
  color: var(--color-danger);
  font-size: 14px;
}

.field-hint {
  display: block;
  margin-top: 5px;
  color: #777;
  font-size: 13px;
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: var(--base-in-indent);
}

@media (max-width: 600px) {
  .tracker-main {
    flex-direction: column;
  }

  .tracker-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
